import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PackageOpen, Save, RefreshCw, CreditCard, ExternalLink } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Eingegangen', color: '#8B7355' },
  { value: 'approved', label: 'Genehmigt', color: '#2F6B4F' },
  { value: 'received', label: 'Ware erhalten', color: '#5D4E3C' },
  { value: 'refunded', label: 'Rückerstattet', color: '#2F6B4F' },
  { value: 'rejected', label: 'Abgelehnt', color: '#B0512E' },
];

const PAYMENT_LABEL = {
  paypal: 'PayPal',
  credit_card: 'Kreditkarte (Stripe)',
  stripe: 'Kreditkarte (Stripe)',
  sofort: 'SOFORT',
  bank_transfer: 'SEPA / Überweisung',
  banktransfer: 'SEPA / Überweisung',
  bank: 'SEPA / Überweisung',
  ueberweisung: 'SEPA / Überweisung',
  cash: 'Bar / Barzahlung',
};

const PAYMENT_HINT = {
  paypal: 'Erstattung über PayPal-Konto: Transaktion suchen → „Rückzahlung senden".',
  credit_card: 'Erstattung über Stripe-Dashboard: Payment öffnen → „Refund".',
  stripe: 'Erstattung über Stripe-Dashboard: Payment öffnen → „Refund".',
  bank_transfer: 'Manuelle SEPA-Überweisung auslösen. IBAN prüfen bevor du überweist.',
  banktransfer: 'Manuelle SEPA-Überweisung auslösen. IBAN prüfen bevor du überweist.',
  bank: 'Manuelle SEPA-Überweisung auslösen. IBAN prüfen bevor du überweist.',
  ueberweisung: 'Manuelle SEPA-Überweisung auslösen. IBAN prüfen bevor du überweist.',
  cash: 'Barzahlung: Erstattung persönlich oder per Überweisung nach Rücksprache.',
  sofort: 'Erstattung ggf. per Überweisung an die hinterlegte IBAN.',
};

const ReturnsManager = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [editing, setEditing] = useState({}); // { [id]: { status, admin_notes, tracking_number, refund_amount } }
  const [saving, setSaving] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/returns/admin/all', {
        params: status ? { status } : {},
      });
      setReturns(res.data?.returns || []);
    } catch (e) {
      toast.error('Retouren konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filterStatus); }, [filterStatus]);

  const patch = (id, key, value) => {
    setEditing((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  const save = async (r) => {
    const changes = editing[r.id] || {};
    if (Object.keys(changes).length === 0) {
      toast.info('Keine Änderungen zum Speichern');
      return;
    }
    setSaving(r.id);
    try {
      await axiosInstance.put(`/returns/admin/${r.id}`, changes);
      toast.success('Aktualisiert · Kunde wurde bei Statusänderung informiert');
      setEditing((prev) => { const n = { ...prev }; delete n[r.id]; return n; });
      await load(filterStatus);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="returns-manager-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-[#8B7355] hover:text-[#5D4E3C] text-sm mb-3"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück
      </button>

      <div className="flex items-center gap-3 mb-4">
        <PackageOpen className="h-7 w-7 text-[#C4A084]" />
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
            Retouren
          </h1>
          <p className="text-sm text-[#8B7355]">Alle Rückgabe-Anträge deiner Kund:innen. Statuswechsel schicken automatisch eine E-Mail.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-[#8B7355]">Filter:</span>
        <button
          type="button"
          onClick={() => setFilterStatus('')}
          className={`text-xs px-3 py-1.5 border ${!filterStatus ? 'bg-[#5D4E3C] text-white border-[#5D4E3C]' : 'border-[#E8DCC8] text-[#5D4E3C] hover:bg-[#FAF5EE]'}`}
        >
          Alle
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setFilterStatus(s.value)}
            className={`text-xs px-3 py-1.5 border ${
              filterStatus === s.value
                ? 'bg-[#5D4E3C] text-white border-[#5D4E3C]'
                : 'border-[#E8DCC8] text-[#5D4E3C] hover:bg-[#FAF5EE]'
            }`}
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(filterStatus)}
          className="ml-auto text-xs px-3 py-1.5 border border-[#E8DCC8] text-[#8B7355] hover:bg-[#FAF5EE] inline-flex items-center gap-1"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#8B7355] py-8 text-center">Lade…</p>
      ) : returns.length === 0 ? (
        <p className="text-sm text-[#8B7355] py-12 text-center border border-[#E8DCC8] bg-white">Keine Retouren in diesem Filter.</p>
      ) : (
        <ul className="space-y-3">
          {returns.map((r) => {
            const changes = editing[r.id] || {};
            const currentStatus = changes.status ?? r.status;
            const info = STATUS_OPTIONS.find((s) => s.value === currentStatus) || STATUS_OPTIONS[0];
            return (
              <li
                key={r.id}
                className="bg-white border border-[#E8DCC8] p-4"
                data-testid={`return-card-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-xs font-mono bg-[#F5EDE4] px-2 py-0.5 text-[#5D4E3C]">{r.ticket}</code>
                      <span className="text-sm font-semibold" style={{ color: info.color }}>{info.label}</span>
                      <span className="text-xs text-[#8B7355]">· Bestellung {r.order_number}</span>
                    </div>
                    <p className="text-xs text-[#8B7355] mt-1">
                      {r.customer_name || r.customer_email} · {new Date(r.created_at).toLocaleString('de-DE')}
                      {r.preferred_solution && ` · Wunsch: ${r.preferred_solution}`}
                    </p>
                    <p className="text-sm text-[#3D3D3D] mt-2 whitespace-pre-line">{r.reason}</p>
                  </div>
                </div>

                {r.order && (
                  <div
                    className="mt-3 border border-[#E8DCC8] bg-[#FAF5EE] p-3 text-xs text-[#5D4E3C]"
                    data-testid={`return-payment-info-${r.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-4 w-4 text-[#8B7355]" />
                      <strong className="uppercase tracking-wider text-[10px] text-[#8B7355]">Zahlungsinfo Bestellung</strong>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                      <div>
                        <span className="text-[#8B7355]">Zahlart:</span>{' '}
                        <strong>{PAYMENT_LABEL[r.order.payment_method] || r.order.payment_method || '–'}</strong>
                      </div>
                      <div>
                        <span className="text-[#8B7355]">Status:</span>{' '}
                        <strong>{r.order.payment_status || '–'}</strong>
                      </div>
                      <div>
                        <span className="text-[#8B7355]">Betrag:</span>{' '}
                        <strong>
                          {(r.order.total ?? r.order.total_amount) != null
                            ? `${formatEUR(Number(r.order.total ?? r.order.total_amount))}`
                            : '–'}
                        </strong>
                      </div>
                      <div>
                        <span className="text-[#8B7355]">Bestell-Status:</span>{' '}
                        <strong>{r.order.status || '–'}</strong>
                      </div>
                      {(r.order.stripe_payment_intent_id || r.order.payment_id) && (
                        <div className="col-span-2 md:col-span-2 truncate">
                          <span className="text-[#8B7355]">Stripe ID:</span>{' '}
                          <code className="font-mono text-[11px]">{r.order.stripe_payment_intent_id || r.order.payment_id}</code>
                        </div>
                      )}
                      {r.order.paypal_order_id && (
                        <div className="col-span-2 md:col-span-2 truncate">
                          <span className="text-[#8B7355]">PayPal Order:</span>{' '}
                          <code className="font-mono text-[11px]">{r.order.paypal_order_id}</code>
                        </div>
                      )}
                      {r.order.iban && (
                        <div className="col-span-2 md:col-span-4 truncate">
                          <span className="text-[#8B7355]">IBAN:</span>{' '}
                          <code className="font-mono text-[11px]">{r.order.iban}</code>
                        </div>
                      )}
                    </div>
                    {PAYMENT_HINT[r.order.payment_method] && (
                      <p className="mt-2 text-[11px] text-[#8B7355] italic">
                        💡 {PAYMENT_HINT[r.order.payment_method]}
                      </p>
                    )}
                    <a
                      href={`/admin/orders?search=${encodeURIComponent(r.order.order_number || r.order_number)}`}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#5D4E3C] hover:text-[#C4A084] underline"
                    >
                      <ExternalLink className="h-3 w-3" /> Bestellung im Order-Manager öffnen
                    </a>
                  </div>
                )}

                <div className="grid md:grid-cols-4 gap-2 mt-4">
                  <select
                    value={currentStatus}
                    onChange={(e) => patch(r.id, 'status', e.target.value)}
                    className="border border-[#E8DCC8] px-2 py-2 text-sm bg-white focus:border-[#C4A084] outline-none"
                    data-testid={`return-status-${r.id}`}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={changes.tracking_number ?? (r.tracking_number || '')}
                    onChange={(e) => patch(r.id, 'tracking_number', e.target.value)}
                    placeholder="Sendungsnummer"
                    className="border border-[#E8DCC8] px-2 py-2 text-sm focus:border-[#C4A084] outline-none"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={changes.refund_amount ?? (r.refund_amount || '')}
                    onChange={(e) => patch(r.id, 'refund_amount', e.target.value)}
                    placeholder="Rückerstattung €"
                    className="border border-[#E8DCC8] px-2 py-2 text-sm focus:border-[#C4A084] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => save(r)}
                    disabled={saving === r.id}
                    className="bg-[#5D4E3C] hover:bg-[#4A3E30] disabled:bg-[#C4A084] text-white px-3 py-2 text-sm inline-flex items-center justify-center gap-1"
                    data-testid={`return-save-${r.id}`}
                  >
                    <Save className="h-4 w-4" /> {saving === r.id ? 'Speichere…' : 'Speichern'}
                  </button>
                </div>
                <textarea
                  value={changes.admin_notes ?? (r.admin_notes || '')}
                  onChange={(e) => patch(r.id, 'admin_notes', e.target.value)}
                  placeholder={'Interne Notizen (bei „Abgelehnt" auch als Grund an Kunde übermittelt)'}
                  rows={2}
                  className="w-full mt-2 border border-[#E8DCC8] px-2 py-2 text-sm focus:border-[#C4A084] outline-none"
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ReturnsManager;
